import React, { useState } from 'react';
import { Plus, X } from 'lucide-react';
import AdminPromptEditor from '../components/AdminPromptEditor';

interface SummarizationParameter {
  id: string;
  name: string;
  description: string;
  type: 'toggle' | 'number' | 'text';
  value: string | number;
  enabled: boolean;
}

interface ModelOption {
  id: string;
  name: string;
  value: string;
}

const AdminSettingsPage: React.FC = () => {
  const [parameters, setParameters] = useState<SummarizationParameter[]>([
    {
      id: 'key-points',
      name: 'Key Points',
      description: 'Extract main points from the document',
      type: 'toggle',
      value: 'important, critical, essential, key',
      enabled: true
    },
    {
      id: 'summary-length',
      name: 'Summary Length',
      description: 'Maximum number of sentences in the summary',
      type: 'number',
      value: 5,
      enabled: true
    },
    {
      id: 'focus-on-results',
      name: 'Focus on Results',
      description: 'Emphasize results and conclusions',
      type: 'toggle',
      value: 'results show, concluded that, findings indicate',
      enabled: false
    },
    {
      id: 'exclude-technical',
      name: 'Exclude Technical Details',
      description: 'Skip highly technical information',
      type: 'toggle',
      value: 'technical specifications, detailed methodology',
      enabled: false
    }
  ]);

  const [showAddModal, setShowAddModal] = useState(false);
  const [newParameter, setNewParameter] = useState<{
    name: string;
    description: string;
    type: 'toggle' | 'number' | 'text';
    value: string;
  }>({
    name: '',
    description: '',
    type: 'toggle',
    value: ''
  });

  // Add state for model selection
  const [selectedModel, setSelectedModel] = useState<string>("bedrock/apac.anthropic.claude-3-5-sonnet-20240620-v1:0");
  const [summaryPrompt, setSummaryPrompt] = useState<string>("");
  const [matchPrompt, setmatchPrompt] = useState<string>("");

  // Prevent editing the match prompt by creating a no-op function
  const noopFunction = () => {
    // This function does nothing, effectively freezing the editor
    console.log("Match Score Prompt editing is disabled");
  };

  // Available model options
  const modelOptions: ModelOption[] = [
    { 
      id: "claude-3-5-sonnet", 
      name: "Claude 3.5 Sonnet", 
      value: "bedrock/apac.anthropic.claude-3-5-sonnet-20240620-v1:0" 
    },
    { 
      id: "claude-3-haiku", 
      name: "Claude 3 Haiku", 
      value: "bedrock/apac.anthropic.claude-3-haiku-20240307-v1:0" 
    },
    { 
      id: "claude-3-opus", 
      name: "Claude 3 Opus", 
      value: "bedrock/apac.anthropic.claude-3-opus-20240229-v1:0" 
    },
    {
      id: "titan-express",
      name: "Amazon Titan Express",
      value: "bedrock/amazon.titan-text-express-v1"
    },
  ];

  // Save settings function
  const saveModelSettings = () => {
    // Store selected model, prompt, and system prompt in localStorage for persistence
    localStorage.setItem('cv-selected-model', selectedModel);
    localStorage.setItem('summary-prompt', summaryPrompt);
    localStorage.setItem('match-prompt', matchPrompt);
    
    // In a real application, you might want to save this to a backend
    alert('Settings saved successfully!');
  };

  // Load settings from localStorage on component mount
  React.useEffect(() => {
    const savedModel = localStorage.getItem('cv-selected-model');
    const savedSummaryPrompt = localStorage.getItem('summary-prompt');
    const savedmatchPrompt = localStorage.getItem('match-prompt');

    if (savedModel) setSelectedModel(savedModel);
    if (savedSummaryPrompt) setSummaryPrompt(savedSummaryPrompt);
    if (savedmatchPrompt) setmatchPrompt(savedmatchPrompt);
  }, []);

  const toggleParameter = (id: string) => {
    setParameters(prev => prev.map(param => 
      param.id === id ? { ...param, enabled: !param.enabled } : param
    ));
  };

  const updateParameterValue = (id: string, value: string | number) => {
    setParameters(prev => prev.map(param => 
      param.id === id ? { ...param, value } : param
    ));
  };

  const addParameter = () => {
    if (!newParameter.name.trim()) return;
    
    const parameter: SummarizationParameter = {
      id: newParameter.name.toLowerCase().replace(/\s+/g, '-'),
      name: newParameter.name,
      description: newParameter.description,
      type: newParameter.type,
      value: newParameter.type,
      enabled: false
    };
    
    setParameters(prev => [...prev, parameter]);
    setNewParameter({ name: '', description: '', type: 'toggle', value: '' });
    setShowAddModal(false);
  };

  const removeParameter = (id: string) => {
    setParameters(prev => prev.filter(param => param.id !== id));
  };

  return (
    <div className="p-6 md:p-8 bg-gray-100 min-h-screen">
      <div className="max-w-6xl mx-auto">
        <div className="bg-white mb-8 p-6 rounded-xl shadow-sm border border-gray-200">
          <h1 className="text-2xl font-semibold text-gray-900">Admin Panel</h1>
          <p className="text-gray-500 mt-1">Configure AI models and summarization parameters</p>
        </div>

        {/* Model Selection Section */}
        <div className="mb-12 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">AI Model Configuration</h2>
          
          <div className="space-y-8">
            {/* Model Dropdown */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select AI Model
              </label>
              <select 
                value={selectedModel}
                onChange={(e) => setSelectedModel(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                title="AI Model Selection"
                aria-label="Select AI Model"
              >
                {modelOptions.map(model => (
                  <option key={model.id} value={model.value}>
                    {model.name}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-sm text-gray-500">
                Select the AI model to use for CV matching and summary generation
              </p>
            </div>

            {/* Summary Prompt Editor */}
            <div className="pt-4">
              <AdminPromptEditor 
                initialContent={summaryPrompt}
                onContentChange={setSummaryPrompt}
                placeholder="Enter the prompt template here..."
                label="Summary Prompt"
                description="This prompt will be used for generating summaries"
              />
            </div>

            {/* Match Score Prompt Editor */}
            <div className="pt-4">
              <div className="relative">
                <div className="absolute inset-0 bg-gray-100 bg-opacity-50 z-10" title="This section is locked"></div>
                <AdminPromptEditor 
                  initialContent={matchPrompt}
                  onContentChange={noopFunction}
                  placeholder="Match Score Prompt (Locked)"
                  label="Match Score Prompt (Read-only)"
                  description="This prompt will be used to calculate the match score between CVs and job descriptions"
                />
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end pt-6">
              <button
                onClick={saveModelSettings}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                Save AI Configuration
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-12">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Summarization Parameters</h2>
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              <Plus className="h-4 w-4" />
              Add Parameter
            </button>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {parameters.map((parameter) => (
              <div key={parameter.id} className="border border-gray-100 rounded-lg shadow-sm bg-gray-50 p-5">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-medium text-gray-900 mb-1">
                      {parameter.name}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {parameter.description}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={parameter.enabled}
                        onChange={() => toggleParameter(parameter.id)}
                        className="sr-only peer"
                        aria-label={`Toggle ${parameter.name}`}
                        title={`Enable or disable ${parameter.name}`}
                        placeholder={`Toggle ${parameter.name}`}
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                    <button
                      onClick={() => removeParameter(parameter.id)}
                      className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                      title={`Remove ${parameter.name}`}
                      aria-label={`Remove ${parameter.name}`}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                <div className="space-y-3">
                  {parameter.type === 'number' ? (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Value
                      </label>
                      <input
                        type="number"
                        value={parameter.value}
                        onChange={(e) => updateParameterValue(parameter.id, parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                        disabled={!parameter.enabled}
                        title={`Value for ${parameter.name}`}
                        placeholder={`Enter value for ${parameter.name}`}
                      />
                    </div>
                  ) : (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Value
                      </label>
                      <textarea
                        value={parameter.value}
                        onChange={(e) => updateParameterValue(parameter.id, e.target.value)}
                        placeholder="Comma-separated keywords (e.g., important, critical)"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none bg-white"
                        rows={3}
                        disabled={!parameter.enabled}
                      />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Add Parameter Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md mx-4">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">New Summarization Parameter</h2>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                  title="Close modal"
                  aria-label="Close modal"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Parameter Name
                  </label>
                  <input
                    type="text"
                    value={newParameter.name}
                    onChange={(e) => setNewParameter(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g. Key Points"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={newParameter.description}
                    onChange={(e) => setNewParameter(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="What this parameter does..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                    rows={3}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Parameter Type
                  </label>
                  <select
                    value={newParameter.type}
                    onChange={(e) => setNewParameter(prev => ({ ...prev, type: e.target.value as 'toggle' | 'number' | 'text' }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    title="Parameter Type"
                    aria-label="Parameter Type"
                  >
                    <option value="toggle">Keyword</option>
                    <option value="number">Number</option>
                    <option value="text">Text</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Default Value
                  </label>
                  {newParameter.type === 'number' ? (
                    <input
                      type="number"
                      value={newParameter.value}
                      onChange={(e) => setNewParameter(prev => ({ ...prev, value: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      title="Parameter value"
                      placeholder="Enter numeric value"
                      aria-label="Parameter numeric value"
                    />
                  ) : (
                    <textarea
                      value={newParameter.value}
                      onChange={(e) => setNewParameter(prev => ({ ...prev, value: e.target.value }))}
                      placeholder="Comma-separated keywords (e.g., important, critical)"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                      rows={3}
                    />
                  )}
                </div>
              </div>

              <div className="flex gap-4 mt-8">
                <button
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={addParameter}
                  className="flex-1 px-4 py-2 bg-blue-400 text-white rounded-lg hover:bg-blue-500 transition-colors"
                >
                  Add Parameter
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-12 text-center text-gray-500 text-sm pb-6">
          <p>© {new Date().getFullYear()} CV Automation Admin Panel</p>
        </div>
      </div>
    </div>
  );
};

export default AdminSettingsPage;